"use client";

/**
 * 运行时状态采集（2026-10-08）。
 *
 * ## 🔴 复用既有 `/api/hermes/runtime`，不新开接口
 *
 * 该端点已经是本项目**唯一**的进程自省入口（看门狗状态、租约、在途计数、
 * ComfyUI 探活），返回结构还带 `problems: string[]`。
 * 首页若自己再写一个 `/api/home/status`，就会出现**两个健康判据**——
 * 页面说「正常」而队列浮窗说「看门狗未运行」，用户不知道信哪个。
 * 判据只能有一份。
 *
 * ## 轮询而不是 SSR 取数
 *
 * `runtimeSummary()` 会同步探活 ComfyUI（2.5s 超时）。放在 server component 里
 * 会拖慢整页首屏，且 HTML 里塞进一个必然过期的状态。
 * 所以：SSR 只给静态骨架，状态区挂载后自己轮询（默认 30s，可手动刷新）。
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "@/lib/api-fetch";
import type { HomeRuntime, HomeServiceStatus, ServiceHealth } from "./types";

/** 与 `runtimeSummary()` 的返回结构对齐（只声明用到的字段，其余忽略）。 */
interface RuntimeSnapshot {
  generatedAt: string;
  ok: boolean;
  problems: string[];
  boot: { bootId: string; pid: number; uptimeMs: number; startedAt: string };
  watchdog: {
    running: boolean;
    userStopped: boolean;
    tickCount: number;
    lastTickAt: string | null;
    stallCount: number;
    lastError: string | null;
  };
  inflight: {
    scenesGenerating: number;
    propsGenerating: number;
    unownedGenerating: number;
    running: number;
  };
  comfy: {
    url: string;
    reachable: boolean;
    latencyMs: number;
    device: string | null;
    vramFreeGB: number | null;
    vramTotalGB: number | null;
    error?: string;
  } | null;
}

const POLL_MS = 30_000;

/**
 * 快照 → 展示模型。
 *
 * 🔴 纯函数、导出并单测：这是全页唯一一处「数字变颜色/文案」的判断，
 * 也是最容易在改文案时改错的地方（比如把 userStopped 判成异常）。
 *
 * 关键语义：
 *   · `comfy === null` 是「**没探测**」（`?probeComfy=0` 或未探测），**不是不可达** ——
 *     标红会让人白跑一趟查机器；
 *   · 用户主动停机（`userStopped`）是**期望状态**，不算异常；
 *   · `unownedGenerating > 0` 是 warn 而非 error：它可能被 30 分钟兜底清扫修掉，
 *     但确实需要人看一眼。
 */
export function toServiceStatuses(snap: RuntimeSnapshot): HomeServiceStatus[] {
  const at = snap.generatedAt ?? new Date().toISOString();
  const out: HomeServiceStatus[] = [];

  // ① 服务进程本体
  out.push({
    id: "portal",
    labelKey: "home.status.portal",
    health: "ok",
    detail: `pid ${snap.boot.pid} · ${Math.round(snap.boot.uptimeMs / 60000)} min`,
    updatedAt: at,
  });

  // ② 看门狗
  {
    const wd = snap.watchdog;
    const health: ServiceHealth = wd.userStopped
      ? "warn"
      : wd.running
        ? wd.stallCount > 0
          ? "warn"
          : "ok"
        : "error";
    out.push({
      id: "watchdog",
      labelKey: "home.status.watchdog",
      health,
      // 🔴 userStopped 给 detailKey（译文「已手动停止」），不给裸 `stoppedByUser`
      //    英文串——其余分支是运维技术细节，保留原文给排查的人看。
      ...(wd.userStopped
        ? { detailKey: "home.status.stoppedByUser" }
        : {
            detail: wd.running
              ? wd.stallCount > 0
                ? `stalls:${wd.stallCount}`
                : `ticks:${wd.tickCount}`
              : (wd.lastError ?? "notRunning"),
          }),
      // 🔴 操作项常驻（2026-10-09）：看门狗行永远给「关闭看门狗」入口，
      //    只有「在跑且没被用户停」才可点——已停/没在跑时置灰，
      //    否则用户想关的时候找不到入口、关完刷新又消失，像功能没做。
      action: "stopWatchdog" as const,
      actionDisabled: wd.userStopped || !wd.running,
      updatedAt: wd.lastTickAt ?? at,
    });
  }

  // ③ 在途作业
  {
    const f = snap.inflight;
    out.push({
      id: "jobs",
      labelKey: "home.status.jobs",
      health: f.unownedGenerating > 0 ? "warn" : "ok",
      detail: `running:${f.running} / unowned:${f.unownedGenerating}`,
      locateHref: "/pipeline",
      updatedAt: at,
    });
  }

  // ④ ComfyUI —— null = 未探测，不是异常
  {
    const c = snap.comfy;
    if (c === null) {
      out.push({
        id: "comfyui",
        labelKey: "home.status.comfyui",
        health: "unknown",
        detailKey: "home.status.notProbed",
        updatedAt: at,
      });
    } else {
      const lowVram = c.vramFreeGB !== null && c.vramTotalGB !== null && c.vramFreeGB / c.vramTotalGB < 0.1;
      out.push({
        id: "comfyui",
        labelKey: "home.status.comfyui",
        health: c.reachable ? (lowVram ? "warn" : "ok") : "error",
        detail: c.reachable
          ? `${c.latencyMs}ms${c.vramFreeGB !== null ? ` · vram ${c.vramFreeGB}/${c.vramTotalGB}G` : ""}`
          : (c.error ?? "unreachable"),
        locateHref: "/settings",
        updatedAt: at,
      });
    }
  }

  return out;
}

export function buildRuntime(snap: RuntimeSnapshot): HomeRuntime {
  const services = toServiceStatuses(snap);
  return {
    services,
    collectedAt: snap.generatedAt ?? new Date().toISOString(),
    hasIssue: services.some((s) => s.health === "error" || s.health === "warn"),
  };
}

export type RuntimeState =
  | { status: "loading"; data: null; error: null }
  | { status: "ok"; data: HomeRuntime; error: null }
  | { status: "error"; data: null; error: string };

export function useRuntimeStatus(pollMs = POLL_MS) {
  const [state, setState] = useState<RuntimeState>({ status: "loading", data: null, error: null });
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/api/hermes/runtime?probeComfy=1", { cache: "no-store" });
      // 该端点在 problems 非空时返回 503 —— 那不是请求失败，是「有异常要报」。
      // 🔴 若按 !res.ok 直接抛，异常服务会显示成「加载失败」，与真实故障混淆。
      const body = (await res.json()) as RuntimeSnapshot;
      if (!alive.current) return;
      setState({ status: "ok", data: buildRuntime(body), error: null });
    } catch (err) {
      if (!alive.current) return;
      setState({
        status: "error",
        data: null,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    if (pollMs <= 0) return () => { alive.current = false; };
    const timer = window.setInterval(() => void load(), pollMs);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [load, pollMs]);

  return { ...state, refresh: load };
}
